import {validateNoMarkdown} from '@github-ui/entity-validators'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  updateIssueCommentBodyMutation,
  UpdateIssueCommentInput,
} from './__generated__/updateIssueCommentBodyMutation.graphql'
import type {updateIssueCommentBodyMutation_Comment$data} from './__generated__/updateIssueCommentBodyMutation_Comment.graphql'

export const updateCommentFragment = graphql`
  fragment updateIssueCommentBodyMutation_Comment on IssueComment {
    id
    author {
      avatarUrl
      login
    }
    authorAssociation
    createdAt
    databaseId
    reactionGroups {
      content
      reactors(first: 5) {
        totalCount
      }
      viewerHasReacted
    }
    viewerCanUpdate
  }
`

export function commitUpdateIssueCommentBodyMutation({
  environment,
  input: {id, body},
  onCompleted,
  onError,
  commentData,
}: {
  environment: Environment
  input: {id: string; body: string | null}
  onCompleted?: () => void
  onError?: (error: Error) => void
  commentData?: updateIssueCommentBodyMutation_Comment$data
}) {
  if (!body) {
    return
  }

  const inputHash: UpdateIssueCommentInput = {
    id,
    body,
  }

  return commitMutation<updateIssueCommentBodyMutation>(environment, {
    mutation: graphql`
      mutation updateIssueCommentBodyMutation($input: UpdateIssueCommentInput!) @raw_response_type {
        updateIssueComment(input: $input) {
          issueComment {
            ...IssueCommentViewerMarkdownViewer
            databaseId
            createdAt
            author {
              avatarUrl
              login
            }
            authorAssociation
            reactionGroups {
              content
              reactors {
                totalCount
              }
              viewerHasReacted
            }
            ...MarkdownEditHistoryViewer_comment
          }
        }
      }
    `,
    variables: {
      input: inputHash,
    },
    optimisticResponse:
      validateNoMarkdown(body) && commentData
        ? {
            updateIssueComment: {
              issueComment: {
                id: commentData.id,
                author: {
                  id: '',
                  avatarUrl: commentData.author?.avatarUrl || '',
                  login: commentData.author?.login || '',
                  __isNode: 'User',
                  __typename: 'User',
                },
                authorAssociation: commentData.authorAssociation,
                body,
                bodyHTML: body,
                createdAt: commentData.createdAt,
                databaseId: commentData.databaseId,
                reactionGroups: (commentData.reactionGroups || []).map(a => {
                  return {
                    content: a.content,
                    reactors: {
                      totalCount: a.reactors.totalCount,
                    },
                    viewerHasReacted: a.viewerHasReacted,
                  }
                }),
                viewerCanUpdate: commentData.viewerCanUpdate,
                viewerCanReadUserContentEdits: true,
                lastEditedAt: new Date().toISOString(),
                __isComment: 'IssueComment',
              },
            },
          }
        : undefined,
    onCompleted: () => onCompleted && onCompleted(),
    onError: error => onError && onError(error),
  })
}
